/* eslint-disable import/no-anonymous-default-export */
import { Box, Card, CardContent, CircularProgress, Stack } from "@mui/material";
import jsonExport from "jsonexport/dist";
import type { Exporter } from "react-admin";
import {
    BulkActionsToolbar,
    BulkDeleteButton,
    BulkExportButton,
    CreateButton,
    downloadCSV,
    ExportButton,
    ListBase,
    ListToolbar,
    Pagination,
    SortButton,
    Title,
    TopToolbar,
    useGetIdentity,
    useListContext,
} from "react-admin";

import { useEffect, useState, useMemo } from "react";
import { useDataProvider } from "react-admin";

import { Transaction, Payment, Fund } from "../../types/types";
import { TransactionEmpty } from "./TransactionEmpty";
import { TransactionListContent } from "./TransactionListContent";
import { TransactionListFilter } from "./TransactionListFilter";

export const TransactionList = () => {
    const { identity } = useGetIdentity();

    if (!identity) return null;

    const isAdmin = identity.role === "admin";

    return (
        <ListBase
            perPage={25}
            sort={{ field: "timestamp", order: "DESC" }} // Adjusted to match `Transaction`
            exporter={exporter}
            filter={isAdmin ? {} : { user_id: identity.id }}
        >
            <TransactionListLayout />
        </ListBase>
    );
};

const TransactionListLayout = () => {
    const { data, isPending, filterValues, sort } = useListContext();
    const { identity } = useGetIdentity();

    // get user names for user ids for admins
    const dataProvider = useDataProvider();

    const [searchTerm, setSearchTerm] = useState("");
    const [enrichedData, setEnrichedData] = useState<Transaction[]>([]);

    useEffect(() => {
        if (!identity || !data) return;

        const entityIds = new Set<string>();

        // Collect unique entity IDs (users & funds)
        data.forEach(tx => {
            entityIds.add(tx.user_id);
            entityIds.add(tx.source);
            entityIds.add(tx.target);
            entityIds.add(tx.asset_id);
        });

        const isAdmin = identity.role === "admin";
        let entitiesMap: Record<string, string> = {};

        // Fetch users & funds in parallel
        Promise.all([
            dataProvider.getMany("users", { ids: Array.from(entityIds) })
                .then(({ data }) => {
                    data.forEach(user => {
                        if (user.first_name && user.last_name) {
                            entitiesMap[user.id] = `${user.first_name} ${user.last_name}`;
                        }
                    });
                }),
            dataProvider.getMany("funds", { ids: Array.from(entityIds) })
                .then(({ data }) => {
                    data.forEach(fund => {
                        if (fund.name) {
                            entitiesMap[fund.id] = fund.name;
                        }
                    });
                })
        ]).then(() => {
            if (!isAdmin) {
                entitiesMap[identity.id] = `${identity.first_name} ${identity.last_name}`;
            }

            console.log(entitiesMap)

            // Enrich transactions with resolved names
            let enriched = data.map(tx => ({
                ...tx,
                user_name: entitiesMap[tx.user_id] || tx.user_id,
                source_name: entitiesMap[tx.source] || tx.source,
                target_name: entitiesMap[tx.target] || tx.target,
                fund_name: entitiesMap[tx.asset_id] || tx.asset_id,
            }));

            setEnrichedData(enriched);
        });

    }, [data, identity, dataProvider]);

    // Dynamically filter transactions based on searchTerm
    const filteredData = useMemo(() => {
        if (!searchTerm) return enrichedData;

        const lowercasedFilter = searchTerm.toLowerCase();

        return enrichedData.filter(tx =>
            Object.values(tx).some(value =>
                typeof value === "string" && value.toLowerCase().includes(lowercasedFilter)
            )
        );
    }, [searchTerm, enrichedData]); // Recomputes only when `searchTerm` or `enrichedData` change


    if (!identity || isPending) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    const hasFilters = filterValues && Object.keys(filterValues).length > 0;
    if (!data?.length && !hasFilters) return <TransactionEmpty />;

    return (
        <Stack direction="row">
            <TransactionListFilter searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <Stack sx={{ width: "100%" }}>
                <Title title={"Transactions"} />
                <ListToolbar actions={<TransactionListActions />} />
                <BulkActionsToolbar>
                    <BulkExportButton />
                    <BulkDeleteButton />
                </BulkActionsToolbar>

                <TransactionListContent txs={filteredData} />
                <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
            </Stack>
        </Stack>
    );
};

const TransactionListActions = () => (
    <TopToolbar>
        <SortButton fields={["timestamp", "amount"]} /> {/* Adjusted fields */}
        <ExportButton />
    </TopToolbar>
);

const exporter: Exporter<Transaction> = async (
    records,
    fetchRelatedRecords
) => {
    const payments = await fetchRelatedRecords<Payment>(
        records,
        "transaction_id",
        "payments"
    );

    const transactions = records.map((transaction) => {
        const payment = payments[transaction.transaction_id];

        return {
            ...transaction,
            status: transaction.status.toUpperCase(), // Normalize case
            amount: transaction.amount.toFixed(2), // Ensure 2 decimal places
            cost_total: Object.values(transaction.cost).reduce(
                (sum, cost) => sum + cost,
                0
            ), // Sum of all costs
            payment_status: payment?.status ?? "Not Paid",
            payment_execution_date: payment?.execution_date ?? "N/A",
            payment_settlement_date: payment?.settlement_date ?? "N/A",
            bank_name: payment?.sender_bank_name ?? "N/A",
            sender_account: payment?.sender_account ?? "N/A",
            recipient_account: payment?.recipient_account ?? "N/A",
            exchange_rate: payment?.exchange_rate
                ? payment.exchange_rate.toFixed(4)
                : "N/A",
        };
    });

    return jsonExport(transactions, {}, (_err: any, csv: string) => {
        downloadCSV(csv, "transactions");
    });
};
